import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface UserRoleInfo {
  loading: boolean;
  userId: string;
  nome: string;
  /** É membro (ativo) da equipe de outro studio → tatuador convidado */
  isTatuador: boolean;
  /** Dono do próprio studio (comportamento padrão) */
  isDono: boolean;
  /** % de comissão que o tatuador paga ao dono (0 se dono) */
  comissaoPercentual: number;
  /** studio_id do dono ao qual o tatuador está vinculado (null se dono) */
  ownerStudioId: string | null;
  /** studio_id "efetivo" pra filtrar dados do studio (ownerStudioId se convidado, o próprio id se dono) */
  effectiveStudioId: string;
  /** 'todos' = vê a base de clientes inteira do studio; 'proprios' = só os que ele atende */
  permissaoClientes: "todos" | "proprios";
  /** 'todos' = vê/usa o estoque do studio; 'nenhum' = sem acesso */
  permissaoEstoque: "todos" | "nenhum";
}

const EMPTY: UserRoleInfo = {
  loading: true,
  userId: "",
  nome: "",
  isTatuador: false,
  isDono: true,
  comissaoPercentual: 0,
  ownerStudioId: null,
  effectiveStudioId: "",
  permissaoClientes: "todos",
  permissaoEstoque: "todos",
};

export function useUserRole(): UserRoleInfo {
  const [info, setInfo] = useState<UserRoleInfo>(EMPTY);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (!cancelled) setInfo({ ...EMPTY, loading: false });
        return;
      }

      const [{ data: u }, { data: membership }] = await Promise.all([
        supabase.from("usuarios").select("nome, role").eq("id", user.id).maybeSingle(),
        supabase
          .from("equipe")
          .select("comissao_percentual, studio_id, status, permissao_clientes, permissao_estoque")
          .eq("tatuador_id", user.id)
          .eq("status", "ativo")
          .maybeSingle(),
      ]);

      const isTatuador = !!membership || u?.role === "tatuador";
      const ownerStudioId = membership?.studio_id ?? null;

      if (!cancelled) {
        setInfo({
          loading: false,
          userId: user.id,
          nome: u?.nome || user.email?.split("@")[0] || "",
          isTatuador,
          isDono: !isTatuador,
          comissaoPercentual: Number(membership?.comissao_percentual) || 0,
          ownerStudioId,
          effectiveStudioId: ownerStudioId ?? user.id,
          permissaoClientes: (membership?.permissao_clientes as "todos" | "proprios") || "proprios",
          permissaoEstoque: (membership?.permissao_estoque as "todos" | "nenhum") || "nenhum",
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}
